import { gql } from "__generated__"
import { useMutation } from "@apollo/client"
import { LoadingButton } from "@mui/lab"
import { Box, Container, Stack, TextField } from "@mui/material"
import { Center, Flex, FlexColumn, InputPassword, Text } from "components/common"
import { useResponsive } from "hooks"
import { enqueueSnackbar } from "notistack"
import { Controller, useForm } from "react-hook-form"
import { useDispatch } from "react-redux"
import { Link } from "react-router-dom"
import { signIn } from "reducers/profileSlice"
import { authRoute } from "routes"

const REGISTER_USER = gql(`
  mutation RegisterUser($name: String!, $username: String!, $password: String!) {
    register(name: $name, username: $username, password: $password) {
      accessToken,
      refreshToken,
      user {
        id
        name
        username
      }
    }
  }
`)

type RegisterFormValues = {
  name: string
  password: string
  username: string
}

const RegisterScreen = () => {
  const dispatch = useDispatch()
  const { isDesktop } = useResponsive()

  const {
    control,
    formState: { isSubmitting },
    handleSubmit,
  } = useForm<RegisterFormValues>({
    defaultValues: {},
  })

  const [registerUser] = useMutation(REGISTER_USER)

  const onSubmit = async (values: RegisterFormValues) => {
    const { username } = values
    await registerUser({
      variables: {
        ...values,
        username: username.trim().toLowerCase(),
      },
    }).then((response) => {
      if (response.data) {
        enqueueSnackbar("Đăng ký thành công")

        const loginData = response.data.register
        dispatch(signIn(loginData))
      }
    })
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter") {
      handleSubmit(onSubmit)()
    }
  }

  return (
    <Container maxWidth="sm">
      <Box
        className="fixed left-0 right-0 top-0 h-[480px] lg:left-[5%] lg:right-[5%] lg:top-[2%] lg:rounded-3xl"
        sx={{ background: `url(${require("assets/images/Airdata_banner.png")}) no-repeat center / cover` }}
      />
      <Stack className="relative z-10 gap-6 rounded-3xl bg-white p-6 lg:p-12">
        <Center>
          <img className="h-[60px]" src={require("assets/icons/Airdata_logo.png")} />
        </Center>
        <Stack>
          <Text className="text-center text-xl font-bold">{isDesktop && "Chào mừng đến với "}Airdata Company</Text>
          <Text className="text-center font-bold text-primary-main">Vui lòng điền thông tin tài khoản</Text>
        </Stack>

        <FlexColumn className="items-center justify-center gap-6">
          <Controller
            control={control}
            name="name"
            render={({ field, fieldState: { error } }) => (
              <TextField fullWidth label="Họ & Tên" required {...field} error={!!error} helperText={error?.message} />
            )}
            rules={{
              required: "Họ & Tên không được để trống",
            }}
          />

          <Controller
            control={control}
            name="username"
            render={({ field, fieldState: { error } }) => (
              <TextField
                fullWidth
                label="Tên đăng nhập"
                required
                {...field}
                error={!!error}
                helperText={error?.message}
              />
            )}
            rules={{
              required: "Tên đăng nhập không được để trống",
            }}
          />

          <Controller
            control={control}
            name="password"
            render={({ field, fieldState: { error } }) => (
              <InputPassword
                fullWidth
                label="Mật khẩu"
                required
                {...field}
                error={!!error}
                helperText={error?.message}
                onKeyDown={handleKeyDown}
              />
            )}
            rules={{
              required: "Mật khẩu không được để trống",
            }}
          />

          <LoadingButton
            color="secondary"
            fullWidth
            loading={isSubmitting}
            onClick={handleSubmit(onSubmit)}
            size="large"
            variant="contained"
          >
            Đăng ký
          </LoadingButton>

          <Flex gap={1}>
            Đã có tài khoản?
            <Link className="font-bold text-primary-main hover:brightness-75" to={authRoute.login.url}>
              Đăng nhập
            </Link>
          </Flex>
        </FlexColumn>
      </Stack>
    </Container>
  )
}

export default RegisterScreen
